/**
 * Theme Configuration
 * Default theme for the Audio DAW canvas UI
 */

import { alpha } from "@mui/material";
import {
  amber,
  blue,
  blueGrey,
  grey,
  lightGreen,
  red,
} from "@mui/material/colors";

export const AF_THEME_DARK = {
  // Theme mode
  mode: "dark",

  // Background colors
  background: alpha(grey[900], 0.95),
  trackBackground: grey[900],
  trackBackgroundDragging: alpha(blue[500], 0.3),
  rulerBackground: grey[900],
  minimapBackground: alpha(grey[900], 0.95),
  minimapTrackBackground: alpha(grey[700], 0.75),

  // Grid
  gridColor: alpha(grey["A100"], 0.05),
  gridLineWidth: 1,

  // Waveform
  defaultWaveformColor: blue[500],
  waveformLineWidth: 1,
  waveformGainScaling: true, // Enable visual scaling of waveform based on gain

  // Track border (when dragging)
  trackBorderColor: blue[500],
  trackBorderWidth: 2,

  // Track labels
  trackLabelColor: grey[300],
  trackLabelFont: "12px sans-serif",
  trackLabelFontWeight: "normal",

  // Track offset display (when dragging)
  offsetDisplayBackground: alpha(grey[900], 0.8),
  offsetDisplayColor: lightGreen["A400"],
  offsetDisplayFont: "12px monospace",
  offsetDisplayFontWeight: "normal",

  // Loading indicator
  loadingTextColor: grey[300],
  loadingTextFont: "14px sans-serif",
  loadingTextFontWeight: "bold",

  // Timeline ruler
  rulerTickColor: grey[700],
  rulerTextColor: grey[300],
  rulerTextFont: "10px monospace",
  rulerTextFontWeight: "normal",
  rulerMajorTickHeight: 38,
  rulerMinorTickHeight: 30,
  rulerTickBottomMargin: 22, // Margin from bottom of canvas where ticks start

  // Playhead
  playheadColor: red["A400"],
  playheadWidth: 2,

  // Minimap
  minimapWaveformOpacity: 0.6,
  minimapViewportFill: alpha(grey["A100"], 0.15),
  minimapViewportStroke: lightGreen["A700"],
  minimapViewportStrokeWidth: 2,
  minimapPlayheadColor: red["A400"],
  minimapPlayheadWidth: 1,

  // Scrollbar (legacy properties for backward compatibility)
  scrollbarTrackColor: alpha(grey["A100"], 0.15),
  scrollbarThumbColor: alpha(grey[300], 0.4),
  scrollbarThumbColorActive: alpha(grey[300], 0.7),
  scrollbarThumbBorder: alpha(grey[900], 0.3),
  scrollbarWidth: 16,

  // Scrollbar configuration object (recommended)
  scrollbar: {
    width: 16, // Thickness of scrollbars (vertical width, horizontal height)
    minThumbSize: 40, // Minimum thumb size in pixels
    trackColor: alpha(grey["A100"], 0.15),
    thumbColor: alpha(grey["A100"], 0.4),
    thumbColorActive: alpha(grey["A100"], 0.7),
    thumbBorder: alpha(grey[900], 0.3),
    showVertical: true,
    showHorizontal: true,
    customDrawFunction: null, // Optional: (ctx, geometry, isActive, theme, orientation) => void
  },

  // Selection
  selectionFill: alpha(amber["A400"], 0.2),
  selectionBorder: amber["A400"],
  selectionBorderWidth: 1,
  selectionHandleColor: amber["A400"],
  selectionHandleWidth: 8,

  // Selection menu
  selectionMenu: {
    background: grey[900],
    border: "transparent",
    boxShadow:
      "0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)",
    fontSize: "14px",
    color: grey[50],
    textColor: grey[50],
    readOnlyTextColor: grey[500],
    mouseEnterBackground: alpha(grey[700], 0.7),
    mouseLeaveBackground: "transparent",
    separatorBackground: alpha(grey[600], 0.9),
    detailsTextColor: grey[200],
    detailsFontSize: "12px",
  },
};

export const AF_THEME_LIGHT = {
  // Theme mode
  mode: "light",

  // Background colors
  background: alpha(grey["A200"], 1),
  trackBackground: grey[50],
  trackBackgroundDragging: alpha(blue[900], 0.15),
  rulerBackground: grey[50],
  minimapBackground: blueGrey[50],
  minimapTrackBackground: grey[50],

  // Grid
  gridColor: alpha(grey[900], 0.1),
  gridLineWidth: 1,

  // Waveform
  defaultWaveformColor: blue[900],
  waveformLineWidth: 1,
  waveformGainScaling: true, // Enable visual scaling of waveform based on gain

  // Track border (when dragging)
  trackBorderColor: blue[900],
  trackBorderWidth: 2,

  // Track labels
  trackLabelColor: grey[900],
  trackLabelFont: "12px sans-serif",
  trackLabelFontWeight: "normal",

  // Track offset display (when dragging)
  offsetDisplayBackground: grey[900],
  offsetDisplayColor: lightGreen["A400"],
  offsetDisplayFont: "12px monospace",
  offsetDisplayFontWeight: "normal",

  // Loading indicator
  loadingTextColor: grey[900],
  loadingTextFont: "14px sans-serif",
  loadingTextFontWeight: "bold",

  // Timeline ruler
  rulerTickColor: grey[700],
  rulerTextColor: grey[900],
  rulerTextFont: "10px monospace",
  rulerTextFontWeight: "normal",
  rulerMajorTickHeight: 38,
  rulerMinorTickHeight: 30,
  rulerTickBottomMargin: 22, // Margin from bottom of canvas where ticks start

  // Playhead
  playheadColor: red["A400"],
  playheadWidth: 2,

  // Minimap
  minimapWaveformOpacity: 0.6,
  minimapViewportFill: alpha(grey["A100"], 0.15),
  minimapViewportStroke: lightGreen[800],
  minimapViewportStrokeWidth: 2,
  minimapPlayheadColor: red["A400"],
  minimapPlayheadWidth: 1,

  // Scrollbar (legacy properties for backward compatibility)
  scrollbarTrackColor: grey[300],
  scrollbarThumbColor: grey[100],
  scrollbarThumbColorActive: grey[200],
  scrollbarThumbBorder: alpha(grey[900], 0.3),
  scrollbarWidth: 16,

  // Scrollbar configuration object (recommended)
  scrollbar: {
    width: 16, // Thickness of scrollbars (vertical width, horizontal height)
    minThumbSize: 40, // Minimum thumb size in pixels
    trackColor: grey[300],
    thumbColor: grey[100],
    thumbColorActive: grey[200],
    thumbBorder: alpha(grey[900], 0.3),
    showVertical: true,
    showHorizontal: true,
    customDrawFunction: null, // Optional: (ctx, geometry, isActive, theme, orientation) => void
  },

  // Selection
  selectionFill: alpha(amber["A400"], 0.2),
  selectionBorder: grey[900],
  selectionBorderWidth: 1,
  selectionHandleColor: amber["A400"],
  selectionHandleWidth: 8,

  // Selection menu
  selectionMenu: {
    background: grey[50],
    border: "transparent",
    boxShadow:
      "0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)",
    fontSize: "14px",
    color: grey[900],
    textColor: grey[900],
    readOnlyTextColor: grey[700],
    mouseEnterBackground: alpha(grey[300], 0.7),
    mouseLeaveBackground: "transparent",
    separatorBackground: alpha(grey[600], 0.9),
    detailsTextColor: grey[900],
    detailsFontSize: "12px",
  },
};
